import createClient from 'openapi-fetch'
import type { components, paths } from './gen/identity'

export type Schemas = components['schemas']
export type Problem = Schemas['Problem']
export type FieldError = Schemas['FieldError']

/** Typed error for every non-2xx response (parsed from RFC 9457 problem+json). */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail?: string
  readonly requestId?: string
  /** field path (e.g. `legal.reg_number`) → first error for that field */
  readonly fieldErrors: Record<string, FieldError>

  constructor(p: { status: number; code: string; detail?: string; requestId?: string; errors?: FieldError[] }) {
    super(p.detail || p.code)
    this.name = 'ApiError'
    this.status = p.status
    this.code = p.code
    this.detail = p.detail
    this.requestId = p.requestId
    this.fieldErrors = {}
    for (const e of p.errors ?? []) this.fieldErrors[e.field] ??= e
  }

  static from(res: Response, body: unknown): ApiError {
    const p = (body && typeof body === 'object' ? body : {}) as Partial<Problem>
    return new ApiError({
      status: res.status,
      code: typeof p.code === 'string' && p.code ? p.code : `http_${res.status}`,
      detail: typeof p.detail === 'string' ? p.detail : undefined,
      requestId: p.request_id || res.headers.get('X-Request-Id') || undefined,
      errors: Array.isArray(p.errors) ? p.errors : undefined,
    })
  }
}

export interface AuthHooks {
  getToken(): string | null
  /** Exchange the refresh cookie for a new access token. Resolves true on success. */
  refresh(): Promise<boolean>
  /** Called when the session cannot be recovered (refresh failed or retry still 401). */
  onAuthLost(): void
}

let hooks: AuthHooks = { getToken: () => null, refresh: async () => false, onAuthLost: () => {} }

export function configureAuth(h: AuthHooks): void {
  hooks = h
}

let inflight: Promise<boolean> | null = null

/** Single-flight refresh: concurrent callers share one POST /session/refresh. */
export function refreshOnce(): Promise<boolean> {
  inflight ??= hooks.refresh().catch(() => false).finally(() => (inflight = null))
  return inflight
}

// /session, /session/refresh, /session/workspace: a 401 there is final, never refresh-and-retry.
const isSessionCall = (url: string) => /^\/api\/identity\/session(\/|$)/.test(new URL(url).pathname)

function withToken(req: Request): Request {
  const token = hooks.getToken()
  if (token) req.headers.set('Authorization', `Bearer ${token}`)
  return req
}

/** fetch with bearer token; on 401 refreshes once and retries the request once. */
export async function authFetch(req: Request): Promise<Response> {
  const retry = req.clone()
  const res = await fetch(withToken(req))
  if (res.status !== 401 || isSessionCall(req.url)) return res
  if (!(await refreshOnce())) {
    hooks.onAuthLost()
    return res
  }
  const res2 = await fetch(withToken(retry))
  if (res2.status === 401) hooks.onAuthLost()
  return res2
}

const origin = typeof location !== 'undefined' ? location.origin : 'http://localhost'

export const identity = createClient<paths>({ baseUrl: `${origin}/api/identity`, fetch: authFetch })

/** Unwrap an openapi-fetch result: data on 2xx, else throw ApiError. Network failures → code `network`. */
export async function call<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let r: { data?: T; error?: unknown; response: Response }
  try {
    r = await p
  } catch (e) {
    throw new ApiError({ status: 0, code: 'network', detail: e instanceof Error ? e.message : String(e) })
  }
  if (!r.response.ok) throw ApiError.from(r.response, r.error)
  return r.data as T
}
